/**
 * O tocador do app: um `<audio>` só, vivo acima de todas as rotas.
 *
 * O Jukebox sabia dizer o que a outra pessoa está ouvindo e mandava para fora,
 * no YouTube Music. Agora, quando existe uma gravação livre da mesma faixa
 * (ver `musica/acervo.ts`), ela toca aqui dentro.
 *
 * Por que acima das rotas, e não dentro do cartão que começou a tocar: um
 * `<audio>` que mora numa tela morre quando a tela troca, e trocar de tela é o
 * que se faz o tempo todo. O elemento é um só, criado uma vez -- e é ele,
 * sozinho, que o sistema operacional conhece: os botões do teclado, a tela de
 * bloqueio e a tela do carro falam com este `<audio>` através da `MediaSession`
 * mais abaixo.
 *
 * O que se vê disto é a `BarraDoTocador`, que mora na raiz do app, ao lado das
 * rotas. A barra é só a vista; quem guarda o estado é este arquivo.
 *
 * O gêmeo no celular é `packages/mobile/src/nucleo/tocador.tsx`: mesma face --
 * `atual`, `tocando`, `tocar`, `alternar`, `parar`, `pausar`, `correr`, `pular`
 * --, outro motor por baixo.
 *
 * ── Um som de cada vez ────────────────────────────────────────────────────
 *
 * A casa faz som por cinco portas: este tocador, o audiolivro, o vídeo do
 * cinema, a chamada do "assistir junto" e o jogo. Até aqui nenhuma delas sabia
 * das outras, e a música seguia tocando por cima do filme.
 *
 * A regra mora nas duas pontas deste arquivo, e não espalhada pelas telas:
 * quando outro som começa, este cala (`aoTocarOutro`); quando este começa, os
 * outros calam (`calarOsOutros`). Cala, não para -- quem apertou uma gravação
 * de 1928 que talvez nunca mais ache não volta e encontra a barra vazia.
 *
 * No navegador isso sai de graça para toda mídia da própria página, inclusive
 * a que ainda não foi escrita: o evento `play` não sobe a árvore, mas a fase de
 * captura no documento vê todos. Quem fica de fora é o que toca dentro de um
 * `<iframe>` de terceiro -- o quadro do filme, o da sessão, o trailer com som e
 * o jogo --, porque um quadro de outra origem não conta o que faz. Esses quatro
 * chamam `pausar()` na mão, e é por isso que ele é público.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  CHAVE_ONDE_PAROU,
  CHAVE_VOLUME,
  VOLUME_MEIO,
  VOLUME_PADRAO,
  guardarOndeParou,
  guardarVolume,
  lerOndeParou,
  lerVolume,
  limitar,
  noCartaz,
  traduzir,
  type EmCartaz,
  type Faixa,
} from '@nosso-sempre/shared';

export type { EmCartaz };

interface Tocador {
  atual: EmCartaz | null;
  tocando: boolean;
  /** Antes do primeiro `canplay`: o acervo é longe e o 78 demora a abrir. */
  abrindo: boolean;
  posicaoMs: number;
  duracaoMs: number;
  erro: string;
  tocar: (o: EmCartaz) => void;
  alternar: () => void;
  parar: () => void;
  /**
   * Cala a música porque outra coisa vai falar, guardando o lugar.
   *
   * Para as telas que tocam som dentro de um `<iframe>` de terceiro, que a
   * página não tem como escutar. O resto não precisa chamar nada.
   */
  pausar: () => void;
  correr: (ms: number) => void;
  /** Adianta ou volta tantos segundos. Negativo volta. */
  pular: (segundos: number) => void;
  /**
   * Troca de faixa dentro do mesmo item do acervo. Negativo volta.
   *
   * Antes da primeira volta ao começo desta, que é o que o botão de anterior
   * faz em todo tocador. Depois da última não faz nada -- o item acabou.
   */
  pularFaixa: (passo: number) => void;
  /**
   * Em qual faixa do item o tocador está agora. Zero quando não há nada.
   *
   * É o que o `atual` não diz: ele é o cartaz *já* montado para esta faixa, e
   * dois cartazes iguais de faixas diferentes seriam indistinguíveis. Quem
   * quiser dizer "3 de 20", ou saltar direto para a catorze, precisa deste
   * número -- e com ele o salto é um `pularFaixa(i - naFaixa)`, sem método novo.
   */
  naFaixa: number;
  /**
   * Em que altura toca, de 0 a 1. Zero é mudo -- não há um segundo estado.
   *
   * O porquê de ser um número só, e não um par "mudo" mais "altura", está em
   * `musica/volume.ts`, junto com o que se guarda no armazém.
   */
  volume: number;
  /** Põe a altura. Apara sozinho o que vier fora de 0..1. */
  mudarVolume: (v: number) => void;
  /** Cala, ou devolve a altura de antes do mudo. */
  alternarMudo: () => void;
}

const Contexto = createContext<Tocador | null>(null);

/** Quanto os botões de pular andam. O mesmo do tocador de audiolivro. */
const PULO = 15;

/**
 * Cala toda mídia da página que não seja a nossa.
 *
 * O mudo fica de fora de propósito: o `<video>` do "assistir junto" é mudo por
 * desenho -- o som da chamada sai por outro elemento --, e pausá-lo derrubaria
 * a imagem de quem está do outro lado sem calar som nenhum.
 */
function calarOsOutros(meu: HTMLMediaElement | null): void {
  for (const m of document.querySelectorAll<HTMLMediaElement>('audio, video')) {
    if (m !== meu && !m.paused && !m.muted) m.pause();
  }
}

export function usarTocador(): Tocador {
  const t = useContext(Contexto);
  if (!t) throw new Error('usarTocador fora do TocadorDeMusica');
  return t;
}

export function TocadorDeMusica({ children }: { children: ReactNode }) {
  const som = useRef<HTMLAudioElement | null>(null);
  /**
   * O cartaz é o que a pessoa pediu; o índice é onde o disco está agora.
   *
   * São duas coisas porque só o cartaz sabe os nomes bons -- os do Last.fm --,
   * e eles valem para uma faixa só. Guardar o cartaz já trocado mataria isso:
   * voltando para a faixa procurada, o nome bom não teria de onde voltar.
   */
  const [cartaz, setCartaz] = useState<EmCartaz | null>(null);
  const [naLista, setNaLista] = useState(0);
  const [tocando, setTocando] = useState(false);
  const [abrindo, setAbrindo] = useState(false);
  const [posicaoMs, setPosicao] = useState(0);
  const [duracaoMs, setDuracao] = useState(0);
  const [erro, setErro] = useState('');
  /**
   * A altura, que volta do armazém já no primeiro desenho.
   *
   * Numa função e não num valor: assim a leitura acontece uma vez, na montagem,
   * e não a cada desenho da árvore inteira. O `try` é o de sempre -- navegador
   * com o armazém trancado existe, e nesse abre-se no som inteiro.
   */
  const [volume, setVolume] = useState(() => {
    try {
      return lerVolume(localStorage.getItem(CHAVE_VOLUME));
    } catch {
      return VOLUME_PADRAO;
    }
  });
  /**
   * A altura de antes do mudo, para o segundo toque devolver o que havia.
   *
   * Numa `ref` e não no armazém de propósito: é memória de sessão. Quem fecha o
   * app mudo volta amanhã mudo -- é o que ficou guardado --, e o primeiro toque
   * no botão devolve o `VOLUME_MEIO`. Só recebe valores acima de zero; ver o
   * `mudarVolume`.
   */
  const antesDoMudo = useRef(VOLUME_MEIO);

  /**
   * Onde a pessoa parou, à espera de a faixa abrir para ser aplicado.
   *
   * O mesmo desenho do audiolivro (`telas/livros/Ouvir.tsx`): a posição não
   * pode ser posta num `<audio>` que ainda não sabe quanto dura, então espera
   * aqui e entra no `loadedmetadata`, uma vez só.
   */
  const retomadaMs = useRef(0);
  /**
   * Se a faixa que voltou do guardado ainda não entrou no `<audio>`.
   *
   * A espera é de propósito. Voltar tocando não é opção -- navegador nenhum
   * deixa, e seria grosseiro --, mas mesmo pôr a fonte no elemento já custaria
   * uma ida ao acervo a cada vez que o app abre, por uma barra que talvez
   * ninguém toque. Então a barra volta desenhada e vazia, e é o primeiro toque
   * no play que abre a faixa, já na altura certa.
   */
  const porAbrir = useRef(false);

  /* Na faixa procurada o `noCartaz` devolve o próprio `cartaz`, então a
     identidade não muda à toa e o aviso ao sistema não se remonta a cada
     volta do relógio. */
  const atual = useMemo(() => (cartaz ? noCartaz(cartaz, naLista) : null), [cartaz, naLista]);
  /** Quantas faixas o item tem. Número, então serve de dependência sozinho. */
  const quantas = cartaz?.gravacao.faixas.length ?? 0;

  /**
   * Põe uma faixa do item no ar. Só isto mexe no `<audio>`.
   *
   * O `desdeMs` é para a retomada, e é o único caminho que não começa do zero:
   * a posição fica marcada e o `loadedmetadata` a aplica. Quem abre uma faixa
   * por vontade própria -- a folha, os botões, o fim da anterior -- não passa
   * dele, e aí abrir é abrir do começo.
   */
  const abrir = useCallback((faixa: Faixa, i: number, desdeMs = 0) => {
    const a = som.current;
    if (!a) return;
    setErro('');
    setNaLista(i);
    setPosicao(desdeMs);
    retomadaMs.current = desdeMs;
    porAbrir.current = false;
    /* A duração que o acervo declarou vale como primeira resposta: a barra já
       nasce com um fim, em vez de piscar de zero para o número quando o
       `loadedmetadata` chegar. O elemento corrige depois, se discordar. */
    setDuracao(faixa.duracaoMs);
    setAbrindo(true);
    a.src = faixa.url;
    /* O navegador só deixa tocar a partir de um toque da pessoa, e é sempre
       esse o caso aqui -- quem chama é o botão da folha. Mas se o bloqueio
       vier, ele vem como promessa recusada e não como erro do elemento, então
       é aqui que se pega. Vale também para a faixa que entra sozinha no fim da
       anterior: aí não há toque nenhum, e o navegador pode recusar. */
    void a.play().catch(() => {
      setAbrindo(false);
      setTocando(false);
      setErro(traduzir('O navegador não deixou tocar. Toque de novo.'));
    });
  }, []);

  const tocar = useCallback(
    (o: EmCartaz) => {
      calarOsOutros(som.current);
      setCartaz(o);
      /* Não dá para passar pelo `pularFaixa` aqui: o `setCartaz` só chega no
         próximo desenho, e a faixa tem de sair do cartaz que acabou de vir. */
      abrir(o.gravacao.faixas[o.gravacao.indice], o.gravacao.indice);
    },
    [abrir],
  );

  const alternar = useCallback(() => {
    const a = som.current;
    /* A faixa que voltou do guardado ainda não está no elemento -- ver o
       `porAbrir`. É este primeiro toque no play que a abre, já na altura
       marcada; daí em diante o botão volta a ser o de sempre. O desvio tem de
       vir antes da guarda abaixo, porque é justamente o `src` que falta. */
    if (porAbrir.current) {
      if (!cartaz) return;
      const faixa = cartaz.gravacao.faixas[naLista];
      if (!faixa) return;
      calarOsOutros(a);
      abrir(faixa, naLista, retomadaMs.current);
      return;
    }
    if (!a || !a.src) return;
    if (a.paused) {
      calarOsOutros(a);
      void a.play().catch(() => setErro(traduzir('Não deu para voltar a tocar.')));
    } else a.pause();
  }, [cartaz, naLista, abrir]);

  const pausar = useCallback(() => {
    som.current?.pause();
  }, []);

  const parar = useCallback(() => {
    const a = som.current;
    if (a) {
      a.pause();
      /* Tira a fonte para o navegador largar a conexão com o acervo; sem isto
         o download continua até o fim mesmo em silêncio. `removeAttribute` e
         não `src = ''`, que no Chrome pede a própria página como áudio. */
      a.removeAttribute('src');
      a.load();
    }
    setCartaz(null);
    setNaLista(0);
    setTocando(false);
    setAbrindo(false);
    setPosicao(0);
    setDuracao(0);
    setErro('');
    /* Fechar a barra é dizer "acabou": no próximo abrir do app não há de quê
       voltar, e deixar o guardado a traria de volta sozinha. */
    retomadaMs.current = 0;
    porAbrir.current = false;
    try {
      localStorage.removeItem(CHAVE_ONDE_PAROU);
    } catch {
      /* Sem armazém não há o que apagar. */
    }
  }, []);

  const correr = useCallback(
    (ms: number) => {
      const a = som.current;
      /* Antes de a faixa guardada abrir o elemento não sabe quanto ela dura,
         mas a barra está desenhada e arrastável -- o fim veio do acervo. Então
         aqui o arrasto move a marca, e é dela que a faixa parte quando abrir.
         Sem isto o dedo puxa e a agulha volta sozinha para onde estava. */
      if (porAbrir.current) {
        if (!cartaz) return;
        const faixa = cartaz.gravacao.faixas[naLista];
        if (!faixa) return;
        const onde = Math.min(Math.max(0, ms), faixa.duracaoMs);
        retomadaMs.current = onde;
        setPosicao(onde);
        return;
      }
      if (!a || !Number.isFinite(a.duration)) return;
      a.currentTime = Math.min(Math.max(0, ms / 1000), a.duration);
      setPosicao(a.currentTime * 1000);
    },
    [cartaz, naLista],
  );

  const pular = useCallback(
    (segundos: number) => {
      const a = som.current;
      /* Mesma história do `correr`: sem faixa no elemento, quem anda é a marca. */
      if (porAbrir.current) {
        correr(retomadaMs.current + segundos * 1000);
        return;
      }
      if (!a || !Number.isFinite(a.duration)) return;
      a.currentTime = Math.min(Math.max(0, a.currentTime + segundos), a.duration);
      setPosicao(a.currentTime * 1000);
    },
    [correr],
  );

  /**
   * A faixa de antes e a de depois, dentro do mesmo item.
   *
   * Isto existe porque o `varrer` já trazia a lista inteira de arquivos do item
   * e jogava tudo fora menos uma: um show do `etree` tem vinte músicas e uma
   * coletânea de netlabel tem quinze, e o tocador parava na primeira. Andar
   * pelo disco não custa pedido nenhum a mais -- a lista já está paga.
   */
  const pularFaixa = useCallback(
    (passo: number) => {
      if (!cartaz) return;
      const i = naLista + passo;
      const faixa = cartaz.gravacao.faixas[i];
      /* Antes da primeira, volta ao começo desta -- é o que o botão de anterior
         faz em todo tocador, e era o que o `previoustrack` já fazia quando o
         item tinha uma faixa só. Depois da última não há para onde ir. */
      if (!faixa) {
        if (passo < 0) correr(0);
        return;
      }
      abrir(faixa, i);
    },
    [cartaz, naLista, abrir, correr],
  );

  /* ── Em que altura ─────────────────────────────────────────────────────
     Mudo é zero, e o porquê disso está em `musica/volume.ts`. */

  /**
   * Põe a altura no elemento e guarda.
   *
   * Um efeito só, preso ao número: o `volume` é propriedade do `<audio>` e não
   * do arquivo, então ele atravessa a troca de `src` sozinho -- não há o que
   * reaplicar quando a faixa muda. E como ninguém mais escreve nele, também não
   * é preciso escutar o `volumechange` para saber que mudou.
   *
   * No iPhone esta linha não faz nada, e é de propósito que se escreve mesmo
   * assim: o Safari do iOS ignora quem tenta mexer no volume por programa --
   * lá quem manda são os botões da lateral. Daí a barra ficar escondida no
   * telefone: é um controle que não teria efeito nenhum.
   */
  useEffect(() => {
    const a = som.current;
    if (a) a.volume = volume;
    try {
      localStorage.setItem(CHAVE_VOLUME, guardarVolume(volume));
    } catch {
      /* Sem armazém a altura vale só esta visita, o que já é o bastante. */
    }
  }, [volume]);

  const mudarVolume = useCallback((v: number) => {
    const novo = limitar(v);
    /* O zero não entra na memória do mudo: guardá-lo faria o botão devolver
       silêncio, que é um botão que não faz nada. */
    if (novo > 0) antesDoMudo.current = novo;
    setVolume(novo);
  }, []);

  const alternarMudo = useCallback(() => {
    setVolume((v) => {
      if (v > 0) {
        antesDoMudo.current = v;
        return 0;
      }
      return antesDoMudo.current;
    });
  }, []);

  /* ── Voltar como estava ────────────────────────────────────────────────
     O que se guarda, e por que o cartaz inteiro: `musica/ondeParou.ts`. */

  /**
   * Escreve onde a pessoa está agora.
   *
   * A altura sai do elemento e não do `posicaoMs`: aquele muda quatro vezes por
   * segundo, e depender dele desmontaria e remontaria o relógio abaixo a cada
   * tique. É o mesmo desvio que o audiolivro faz.
   */
  const guardarLugar = useCallback(() => {
    if (!cartaz) return;
    const a = som.current;
    /* Enquanto a faixa não entrou no elemento, quem sabe a altura é a marca da
       retomada: o `<audio>` está vazio e diria zero, apagando no primeiro tique
       justamente o que se quer guardar. */
    const ms = porAbrir.current ? retomadaMs.current : (a?.currentTime ?? 0) * 1000;
    try {
      localStorage.setItem(CHAVE_ONDE_PAROU, guardarOndeParou(cartaz, naLista, ms));
    } catch {
      /* Perder onde parou não é motivo para atrapalhar quem está ouvindo. */
    }
  }, [cartaz, naLista]);

  /**
   * De dez em dez segundos, e quando a página vai embora.
   *
   * Dez porque escrever a cada tique do tocador seria escrever quatro vezes por
   * segundo, e perder dez segundos de um show não incomoda ninguém -- é a conta
   * que o audiolivro já fazia. O `pagehide` fecha essa janela quando dá, e o
   * `visibilitychange` é o que vale no celular, onde a aba some sem avisar que
   * vai fechar.
   *
   * De propósito não se guarda na limpeza deste efeito: ela roda também quando
   * a faixa troca, e nesse instante o índice antigo já convive com o elemento
   * zerado da faixa nova -- gravaria a faixa de antes começando do zero.
   */
  useEffect(() => {
    if (!cartaz) return;
    /* Uma vez já na entrada: trocar de faixa e fechar o app antes do primeiro
       tique deixaria guardada a faixa anterior. Aqui o elemento ou está em zero
       -- que é onde a faixa nova de facto começa -- ou na marca da retomada. */
    guardarLugar();
    const relogio = window.setInterval(guardarLugar, 10_000);
    const aoSair = () => guardarLugar();
    const aoEsconder = () => {
      if (document.hidden) guardarLugar();
    };
    window.addEventListener('pagehide', aoSair);
    document.addEventListener('visibilitychange', aoEsconder);
    return () => {
      window.clearInterval(relogio);
      window.removeEventListener('pagehide', aoSair);
      document.removeEventListener('visibilitychange', aoEsconder);
    };
  }, [cartaz, guardarLugar]);

  /**
   * Ao abrir o app, a barra volta como estava: desenhada, parada e ainda vazia.
   *
   * Roda uma vez, antes de qualquer toque ser possível, então não há corrida com
   * o relógio acima. O `<audio>` fica intocado de propósito -- ver o `porAbrir`.
   */
  useEffect(() => {
    let guardado: string | null = null;
    try {
      guardado = localStorage.getItem(CHAVE_ONDE_PAROU);
    } catch {
      /* Sem armazém, começa do zero -- que é como o tocador já sabe começar. */
    }
    const onde = lerOndeParou(guardado);
    if (!onde) return;
    const faixa = onde.cartaz.gravacao.faixas[onde.naFaixa];
    if (!faixa) return;
    setCartaz(onde.cartaz);
    setNaLista(onde.naFaixa);
    setPosicao(onde.ms);
    /* O fim vem do acervo, como no `abrir`: é o que deixa a linha do tempo
       nascer inteira em vez de esperar o elemento que ainda não vai abrir. */
    setDuracao(faixa.duracaoMs);
    retomadaMs.current = onde.ms;
    porAbrir.current = true;
  }, []);

  /**
   * O outro lado da regra: som alheio começou, a música cala.
   *
   * Um ouvinte só, no documento e na fase de captura, vale por todas as telas
   * que tocam alguma coisa -- as de hoje e as que ainda não foram escritas. O
   * `play` não sobe a árvore, então escutar na raiz sem a captura não pegaria
   * nada; com ela, o documento vê o evento antes de ele chegar ao elemento.
   *
   * Pausa, e não para: quem estava ouvindo volta do filme e aperta o play de
   * novo, no mesmo ponto.
   */
  useEffect(() => {
    const meu = som.current;
    const aoTocarOutro = (e: Event) => {
      const quem = e.target;
      if (quem === meu || !meu || meu.paused) return;
      if (quem instanceof HTMLMediaElement && !quem.muted) meu.pause();
    };
    document.addEventListener('play', aoTocarOutro, true);
    return () => document.removeEventListener('play', aoTocarOutro, true);
  }, []);

  /**
   * O que o sistema operacional vê.
   *
   * É esta a peça que faz a faixa aparecer na tela de bloqueio, no centro de
   * controle e -- com o telefone ligado no carro -- na tela do CarPlay. Sem os
   * `setActionHandler`, o botão de tocar do volante não tem com quem falar e o
   * sistema mostra a faixa sem controle nenhum.
   *
   * A capa vai em três tamanhos com o mesmo endereço de propósito: o Last.fm
   * serve uma imagem só, e quem escolhe entre as medidas é o sistema. Declarar
   * uma medida só faz o iOS recusar a arte inteira.
   */
  useEffect(() => {
    const ms = navigator.mediaSession;
    if (!ms) return;
    if (!atual) {
      ms.metadata = null;
      ms.playbackState = 'none';
      return;
    }
    ms.metadata = new MediaMetadata({
      title: atual.titulo,
      artist: atual.artista,
      album: atual.gravacao.album,
      artwork: atual.capaUrl
        ? [
            { src: atual.capaUrl, sizes: '96x96', type: 'image/jpeg' },
            { src: atual.capaUrl, sizes: '256x256', type: 'image/jpeg' },
            { src: atual.capaUrl, sizes: '512x512', type: 'image/jpeg' },
          ]
        : [],
    });
    ms.playbackState = tocando ? 'playing' : 'paused';
  }, [atual, tocando]);

  useEffect(() => {
    const ms = navigator.mediaSession;
    if (!ms) return;
    const liga = (acao: MediaSessionAction, quem: MediaSessionActionHandler | null) => {
      /* Nem todo navegador conhece toda ação, e pedir uma que ele não conhece
         estoura em vez de ser ignorada. */
      try {
        ms.setActionHandler(acao, quem);
      } catch {
        /* sem esta ação neste navegador */
      }
    };
    liga('play', () => alternar());
    liga('pause', () => alternar());
    liga('stop', () => parar());
    liga('seekbackward', () => pular(-PULO));
    liga('seekforward', () => pular(PULO));
    /* Arrastar a linha do tempo do próprio sistema, e não só pular de quinze em
       quinze. Passou a valer a pena quando o show inteiro virou uma faixa: achar
       um trecho no meio de duas horas e meia aos pulinhos é outra coisa que achá-lo
       numa faixa de três minutos. Sem esta ação a linha aparece, mas o dedo
       arrasta e ela volta sozinha. */
    liga('seekto', (detalhes) => {
      if (typeof detalhes.seekTime !== 'number') return;
      correr(detalhes.seekTime * 1000);
    });
    liga('previoustrack', () => pularFaixa(-1));
    /* O de avançar só quando há para onde: num 78 de uma faixa só, registrar a
       ação faz o carro e a tela de bloqueio desenharem um botão que não leva a
       lugar nenhum. O de voltar fica sempre, porque ele volta ao começo. */
    liga('nexttrack', quantas > 1 ? () => pularFaixa(1) : null);
    return () => {
      for (const a of [
        'play',
        'pause',
        'stop',
        'seekbackward',
        'seekforward',
        'seekto',
        'previoustrack',
        'nexttrack',
      ] as MediaSessionAction[]) {
        liga(a, null);
      }
    };
  }, [alternar, parar, pular, correr, pularFaixa, quantas]);

  /** Onde a agulha está, para o sistema desenhar a linha do tempo sozinho. */
  useEffect(() => {
    const ms = navigator.mediaSession;
    if (!ms?.setPositionState || duracaoMs <= 0) return;
    try {
      ms.setPositionState({
        duration: duracaoMs / 1000,
        position: Math.min(posicaoMs, duracaoMs) / 1000,
        playbackRate: 1,
      });
    } catch {
      /* posição impossível enquanto o elemento ainda se acerta */
    }
  }, [posicaoMs, duracaoMs]);

  /**
   * O teclado do computador, que era o único controle que faltava.
   *
   * O volante do carro, a tela de bloqueio e os botões do fone já falam com o
   * tocador pelo `mediaSession` logo acima. No computador não havia nada: com a
   * barra tocando lá embaixo e a mão no teclado, pausar queria dizer achar o
   * botão com o ponteiro. Espaço pausa, as setas correm quinze segundos -- os
   * mesmos quinze do `seekbackward`, para que o pulo seja um só no app inteiro
   * -- e o `M` cala.
   *
   * ── Por que o volume não fica nas setas de cima e de baixo ────────────────
   *
   * Seria o par natural do `M`, e é justamente por isso que não vai: ↑ e ↓ são
   * as teclas de rolar a página, e esta casa é feita de listas compridas --
   * o acervo, a estante, a fileira do cinema. Tomá-las enquanto a música toca
   * quebraria a rolagem no meio da leitura, que é pior do que não ter atalho.
   *
   * O espaço se defende porque é convenção de toda a web (YouTube, Netflix,
   * Spotify fazem igual); seta-como-volume não é: o YouTube só a liga quando o
   * foco está no próprio quadro, e o Spotify não a liga. Quem quiser as setas
   * tem a barra: com ela em foco, elas andam sozinhas, e o `[role="slider"]` da
   * guarda acima é o que garante que este atalho não atrapalhe.
   *
   * ── Onde ele se cala, que é a parte que importa ───────────────────────────
   *
   * Um atalho global sem freio rouba o teclado do resto do app: espaço é o que
   * aperta um botão em foco, e seta é o que anda numa barra, numa fileira de
   * abas e num campo de texto. Por isso ele sai de cena quando:
   *
   * - não há faixa nenhuma (`atual`), e aí não existe atalho a roubar;
   * - alguém já tratou a tecla (`defaultPrevented`), ou veio com ⌘/Ctrl/Alt,
   *   que é atalho do navegador e não nosso;
   * - há uma `<dialog open>` na tela. A folha das faixas e o visor de fotos são
   *   `<dialog>` de verdade; o visor anda de foto com as setas e **não** chama
   *   `preventDefault()`, então sem esta linha a seta trocaria a foto e correria
   *   a música na mesma batida. Enquanto uma janela está aberta, o teclado é
   *   dela;
   * - o foco está num campo, num botão, num link ou numa barra -- inclusive a
   *   própria linha do tempo do tocador, que é um `input[type=range]` e já anda
   *   sozinha com as setas.
   *
   * O `preventDefault()` só sai quando ele de fato agiu, para que a tecla que
   * ele deixou passar continue valendo o que valia (espaço rola a página).
   */
  useEffect(() => {
    if (!atual) return;
    function naTecla(e: KeyboardEvent) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('dialog[open]')) return;
      const foco = document.activeElement;
      if (foco instanceof HTMLElement && foco.isContentEditable) return;
      if (
        foco?.closest(
          'input, textarea, select, button, a[href], summary, [role="button"], [role="slider"], [role="tab"]',
        )
      )
        return;
      if (e.key === ' ') {
        e.preventDefault();
        alternar();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        pular(-PULO);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        pular(PULO);
      } else if (e.key === 'm' || e.key === 'M') {
        /* As duas letras porque o `key` traz o que saiu do teclado: com o
           Shift preso, ou com o Caps travado, vem `M` maiúsculo. */
        e.preventDefault();
        alternarMudo();
      }
    }
    window.addEventListener('keydown', naTecla);
    return () => window.removeEventListener('keydown', naTecla);
  }, [atual, alternar, pular, alternarMudo]);

  const valor = useMemo<Tocador>(
    () => ({
      atual,
      tocando,
      abrindo,
      posicaoMs,
      duracaoMs,
      erro,
      tocar,
      alternar,
      parar,
      pausar,
      correr,
      pular,
      pularFaixa,
      naFaixa: naLista,
      volume,
      mudarVolume,
      alternarMudo,
    }),
    [
      atual,
      tocando,
      abrindo,
      posicaoMs,
      duracaoMs,
      erro,
      tocar,
      alternar,
      parar,
      pausar,
      correr,
      pular,
      pularFaixa,
      naLista,
      volume,
      mudarVolume,
      alternarMudo,
    ],
  );

  return (
    <Contexto.Provider value={valor}>
      {/* Sem `controls`: quem desenha é a barra. Fica na árvore o tempo todo
          para que trocar de tela não leve o som junto. */}
      <audio
        ref={som}
        preload="none"
        onPlay={() => {
          setTocando(true);
          setAbrindo(false);
        }}
        onPause={() => setTocando(false)}
        onPlaying={() => setAbrindo(false)}
        onWaiting={() => setAbrindo(true)}
        onTimeUpdate={(e) => setPosicao(e.currentTarget.currentTime * 1000)}
        onLoadedMetadata={(e) => {
          const a = e.currentTarget;
          const d = a.duration;
          if (Number.isFinite(d) && d > 0) setDuracao(d * 1000);
          /* Só agora o elemento sabe quanto a faixa dura, e só agora a altura
             guardada pode entrar. Vale uma vez: zerar a marca aqui é o que
             impede a próxima faixa do disco de começar na mesma altura desta. */
          if (retomadaMs.current > 0) {
            a.currentTime = retomadaMs.current / 1000;
            setPosicao(retomadaMs.current);
            retomadaMs.current = 0;
          }
        }}
        /* A faixa acabou: segue para a próxima do item. Na última o
           `pularFaixa` não acha faixa e não faz nada, e quem fica é o cartaz
           com o botão de tocar de novo -- o site faz o mesmo, e fechar a barra
           sozinha tiraria a chance de repetir uma gravação de 1928 que a pessoa
           pode nunca mais achar. */
        onEnded={() => {
          setTocando(false);
          setPosicao(0);
          pularFaixa(1);
        }}
        onError={() => {
          setAbrindo(false);
          setTocando(false);
          setErro(traduzir('A gravação não abriu.'));
        }}
      />
      {children}
    </Contexto.Provider>
  );
}
